import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreDivider,
  OreHeading,
  OreStack,
  OreText,
  SvgArrowWidget,
} from '@edenredespana/oreneta'
import { openNavigationRoutes } from '@/Shared/ui/routers/data/navigationRoutes'
import { useNavigate } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { LoginTranslation } from '@/Account/ui/accountTranslation'

import { createPasswordRecoveryRepository } from '@/Account/infrastructure/ApiPasswordRecoveryRepository'
import { usePasswordRecoveryController } from '@/Account/ui/hooks'
import { PasswordRecoveryBasicForm } from '@/Account/ui/molecules'
import { PasswordRecoveryData } from '@/Account/ui/hooks'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useState } from 'react'
import { useLoader } from '@/Shared/ui/loader'
import { useRateLimitedCountdown } from "@/Shared/ui/hooks"
import { HttpMethod } from '@/Shared/domain'
import { passwordRecovery } from '@/Shared/infrastructure/endpoints'
import { IndentifyAffiliateErrorModal } from './IndentifyAffiliateErrorModal'

export const PasswordRecoveryForm = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoader()
  const { form, verifyIdentity } = usePasswordRecoveryController(createPasswordRecoveryRepository())
  const { formState: { isValid } } = form
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState<boolean>(false)

  const {
    formattedMessage,
    startCountdown,
    isBlocked,
    shouldBlockForm,
  } = useRateLimitedCountdown({
    messageTemplate: t(formTranslation.common.validationBlockMessage),
    params: {
      endpointRoute: `/${passwordRecovery}`,
      endpointActionName: HttpMethod.POST
    }
  })

  const handleSubmit = async (data: PasswordRecoveryData) => {
    startLoading()
    const success = await verifyIdentity(data)
    stopLoading()

    if (!success) {
      setIsErrorMessageOpen(true)

      const mustBlockForm = await shouldBlockForm()
      if (mustBlockForm) {
        startCountdown()
      }
    }
  }

  return (
    <>
      <FormProvider {...form}>
        <OreStack style={{ width: '50%' }}>
          <OreStack space="small" direction="column" placeContent="stretch" placeItems="start">
            <OreHeading as="h3" size="headline-lg">
              {t(LoginTranslation.recovery.pageTitle)}
            </OreHeading>
          </OreStack>
          <OreDivider />
          <form>
            <OreCardContainer>
              <OreCardWrapper space="large">
                <OreStack space="large" placeContent="stretch" placeItems="start">
                  <OreStack space="small" placeContent="stretch">
                    <OreHeading as="h4" size="headline-md">
                      {t(LoginTranslation.recovery.form.title)}
                    </OreHeading>
                    <OreText size="text-md" tone="neutral-600">
                      <Trans t={t}>
                        {t(LoginTranslation.recovery.form.subtitle)}
                      </Trans>
                    </OreText>
                    <PasswordRecoveryBasicForm isBlocked={isBlocked} errorMessage={formattedMessage} />
                  </OreStack>
                </OreStack>
              </OreCardWrapper>
            </OreCardContainer>
            <OreDivider space="medium" />
            <OreStack space="large" placeContent="space-between" placeItems="center" direction="row">
              <OreButton
                onClick={() => navigate(openNavigationRoutes.login)}
                category="tertiary"
                icon={<SvgArrowWidget />}
                iconAlign="left"
                data-testid="go-back"
              >
                {t(formTranslation.common.buttons.back)}
              </OreButton>
              <OreButton
                disabled={!isValid || isBlocked}
                type="submit"
                onClick={form.handleSubmit(handleSubmit)}
                size="large"
                category="primary"
                data-testid="continue"
              >
                {t(formTranslation.common.buttons.continue)}
              </OreButton>
            </OreStack>
          </form>
        </OreStack>
      </FormProvider>
      <IndentifyAffiliateErrorModal
        isErrorMessageOpen={isErrorMessageOpen}
        setOpen={setIsErrorMessageOpen}
      />
    </>
  )
}
